import React, { useState } from 'react';
import { 
  Avatar, 
  Button, 
  Typography, 
  Card, 
  List, 
  Tag,
  Space,
  Modal,
  Form,
  Select,
  message,
  Radio
} from 'antd';
import { 
  UserOutlined,
  LeftOutlined,
  TagOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

// 更新客户数据接口
interface CustomerData {
  id: string;
  name: string;
  phone: string;
  status: string; // 'unconsumed' 或 'consumed'
  createdAt: string;
  orderAmount?: number;
  tags?: string[];
  // 新增字段
  butlerName?: string; // 绑定的管家姓名
  butlerBindTime?: string; // 绑定管家的时间
  orderCountAfterBinding?: number; // 绑定管家后的订单数量
  consumptionAfterBinding?: number; // 绑定管家后的消费金额
}

// 模拟客户数据
const initialCustomers: CustomerData[] = [
  {
    id: '1',
    name: '客户A',
    phone: '136****8000',
    status: 'unconsumed', // 改为未消费
    createdAt: '2023-05-15',
    tags: ['高消费', '生日特权'],
    butlerName: '李管家',
    butlerBindTime: '2023-05-16',
    orderCountAfterBinding: 0, // 未消费
    consumptionAfterBinding: 0
  },
  {
    id: '2',
    name: '客户B',
    phone: '135****9000',
    status: 'consumed', // 改为已消费
    createdAt: '2023-05-18',
    orderAmount: 2580,
    tags: ['新客户'],
    butlerName: '张管家',
    butlerBindTime: '2023-05-20',
    orderCountAfterBinding: 1,
    consumptionAfterBinding: 2580
  },
  {
    id: '3',
    name: '客户C',
    phone: '139****7000',
    status: 'consumed', // 改为已消费
    createdAt: '2023-05-20',
    orderAmount: 3200,
    tags: ['定期消费'],
    butlerName: '王管家',
    butlerBindTime: '2023-05-22',
    orderCountAfterBinding: 2,
    consumptionAfterBinding: 3200
  },
];

const CustomerListPage: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<CustomerData[]>(initialCustomers);
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerData | null>(null);
  const [tagForm] = Form.useForm();
  
  // 添加客户状态筛选
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // 预定义标签列表
  const predefinedTags = [
    '高消费', '新客户', '定期消费', '生日特权', '商务客户', 
    '休闲旅游', '长住客人', '需特殊关注', '敏感客户', 'VIP客户'
  ];
  
  // 处理打开标签模态框
  const handleOpenTagModal = (customer: CustomerData) => {
    setSelectedCustomer(customer);
    setTagModalVisible(true);
    tagForm.setFieldsValue({
      tags: customer.tags || []
    });
  };

  // 处理标签提交
  const handleTagSubmit = () => {
    tagForm.validateFields().then(values => {
      // 更新客户标签
      const updatedCustomers = customers.map(c => {
        if (c.id === selectedCustomer?.id) {
          return { ...c, tags: values.tags };
        }
        return c;
      });
      
      setCustomers(updatedCustomers);
      message.success('标签设置成功!');
      setTagModalVisible(false);
    });
  };
  
  // 获取客户状态颜色和文本
  const getCustomerStatusInfo = (status: string) => {
    switch (status) {
      case 'unconsumed':
        return { color: 'default', text: '未消费' };
      case 'consumed':
        return { color: 'success', text: '已消费' };
      default:
        return { color: 'default', text: '未知' };
    }
  };
  
  // 筛选客户列表
  const filteredCustomers = customers.filter(customer => {
    if (statusFilter === 'all') return true;
    return customer.status === statusFilter;
  });
  
  // 计算未消费和已消费数量
  const unconsumedCount = customers.filter(c => c.status === 'unconsumed').length;
  const consumedCount = customers.filter(c => c.status === 'consumed').length;

  return (
    <div style={{ 
      maxWidth: 500, 
      margin: '0 auto', 
      background: '#f5f5f5', 
      minHeight: '100vh'
    }}>
      {/* 顶部标题栏 */}
      <div style={{ 
        padding: '12px 16px', 
        background: '#fff', 
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #f0f0f0',
      }}>
        <Button 
          type="text" 
          icon={<LeftOutlined />} 
          onClick={() => navigate(-1)}
          style={{ marginRight: 16 }}
        />
        <div style={{ fontSize: 16, fontWeight: 'bold' }}>我的客户</div>
      </div>
      
      {/* 主内容区 */}
      <div style={{ padding: '16px' }}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <Title level={5} style={{ margin: 0 }}>客户列表</Title>
            <Text type="secondary">共 {customers.length} 位</Text>
          </div>
          
          {/* 客户状态统计 */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <Text type="secondary">未消费</Text>
                <div style={{ fontSize: 18, fontWeight: 'bold', color: '#666' }}>{unconsumedCount}</div>
              </div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <Text type="secondary">已消费</Text>
                <div style={{ fontSize: 18, fontWeight: 'bold', color: '#52c41a' }}>{consumedCount}</div>
              </div>
            </div>
          </div>
          
          {/* 添加客户状态筛选 */}
          <div style={{ marginBottom: 16 }}>
            <Radio.Group 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              buttonStyle="solid"
              style={{ width: '100%', display: 'flex' }}
            >
              <Radio.Button value="all" style={{ flex: 1, textAlign: 'center', borderRadius: '4px 0 0 4px' }}>全部</Radio.Button>
              <Radio.Button value="unconsumed" style={{ flex: 1, textAlign: 'center', borderRadius: 0 }}>未消费</Radio.Button>
              <Radio.Button value="consumed" style={{ flex: 1, textAlign: 'center', borderRadius: '0 4px 4px 0' }}>已消费</Radio.Button>
            </Radio.Group>
          </div>
          
          <List
            itemLayout="horizontal"
            dataSource={filteredCustomers}
            renderItem={item => {
              const statusInfo = getCustomerStatusInfo(item.status);
              return (
                <List.Item
                  actions={[
                    <Button 
                      type="link" 
                      size="small" 
                      icon={<TagOutlined />}
                      onClick={() => handleOpenTagModal(item)}
                    >
                      标签
                    </Button>,
                    <Button type="link" size="small">详情</Button>
                  ]}
                >
                  <List.Item.Meta
                    avatar={<Avatar icon={<UserOutlined />} />}
                    title={
                      <Space>
                        <span>{item.name}</span>
                        <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <div>电话: {item.phone}</div>
                        <div>时间: {item.createdAt}</div>
                        {item.orderAmount && (
                          <div>总消费金额: ¥{item.orderAmount.toFixed(2)}</div>
                        )}
                        {item.status === 'consumed' && item.orderCountAfterBinding && item.consumptionAfterBinding ? (
                          <div>
                            <div>消费次数: {item.orderCountAfterBinding}单</div>
                            <div>消费金额: ¥{item.consumptionAfterBinding.toFixed(2)}</div>
                          </div>
                        ) : null}
                        {item.tags && item.tags.length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            {item.tags.map(tag => (
                              <Tag color="blue" key={tag} style={{ marginRight: 4 }}>
                                {tag}
                              </Tag>
                            ))}
                          </div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </Card>
      </div>

      {/* 客户标签设置模态框 */}
      <Modal
        title="客户标签设置"
        open={tagModalVisible}
        onOk={handleTagSubmit}
        onCancel={() => setTagModalVisible(false)}
        okText="保存"
        cancelText="取消"
        okButtonProps={{ style: { backgroundColor: '#529C8A', borderColor: '#529C8A' } }}
      >
        {selectedCustomer && (
          <div style={{ marginBottom: 16 }}>
            <p><strong>客户姓名:</strong> {selectedCustomer.name}</p>
            <p><strong>联系方式:</strong> {selectedCustomer.phone}</p>
          </div>
        )}
        <Form form={tagForm}>
          <Form.Item
            name="tags"
            label="标签"
            rules={[{ required: true, message: '请至少选择一个标签' }]}
          >
            <Select
              mode="multiple"
              placeholder="请选择标签"
              style={{ width: '100%' }}
              tokenSeparators={[',']}
            >
              {predefinedTags.map(tag => (
                <Select.Option key={tag} value={tag}>
                  {tag}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CustomerListPage; 